import React from 'react';
import { TimelineNode } from '../App';
import Icon from './Icon';
import './Timeline.css';

/**
 * Timeline 组件
 * 
 * 功能：
 * - 显示交互式时间轴，展示恋爱历程的重要时刻
 * - 节点按时间倒序排列（最新在顶部）
 * - 男/女视角分别显示在左侧/右侧
 * - 阶段变化标识（相识、相知、相恋、相守）
 * - 点击节点显示详情
 * 
 * 特性：
 * - 响应式设计（PC/移动端适配）
 * - 男女视角颜色区分（男：浅蓝，女：粉红）
 * - 脉冲动画效果
 * - 箭头指向时间轴线
 */

interface TimelineProps {
  nodes: TimelineNode[]; // 时间轴节点数组
  selectedId: string | undefined; // 当前选中的节点ID
  onNodeClick: (node: TimelineNode) => void; // 节点点击回调
}

const Timeline: React.FC<TimelineProps> = ({ nodes, selectedId, onNodeClick }) => {
  // 按日期降序排列节点（最新在顶部）
  const sortedNodes = [...nodes].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
  
  // 反转数组用于阶段判断（最新在底部）
  const reversedForStages = [...sortedNodes].reverse();
  
  // 提取阶段变化点（基于反转后的数组）
  const stageChanges: {stage: string, index: number}[] = [];
  reversedForStages.forEach((node, index) => {
    if (node.stage) {
      // 检查是否是新阶段的开始
      const isNewStage = index === 0 || reversedForStages[index - 1].stage !== node.stage;
      
      // 在新阶段的第一个节点后放置阶段标识
      if (isNewStage) {
        // 转换回原始索引
        const originalIndex = sortedNodes.length - 1 - index;
        stageChanges.push({ stage: node.stage, index: originalIndex + 0.5 });
      }
    }
  });
  
  return (
    <div className="timeline">
      {/* 时间轴线 */}
      <div className="timeline-line" ><span className='timeline-heart-arrow'/></div>
      
      {/* 时间轴节点容器 */}
      <div className="timeline-items">
        {sortedNodes.map((node, index) => (
          <React.Fragment key={node.id}>
            {/* 时间轴节点 */}
            <div
              className={`timeline-item ${selectedId === node.id ? 'selected' : ''} ${node.perspective === 'male' ? 'left' : 'right'} ${node.perspective}`}
              onClick={() => onNodeClick(node)}
              data-node-id={node.id}
            >
              {/* 时间轴点 */}
              <div className="timeline-dot" />
              
              {/* 节点内容 */}
              <div className="timeline-content">
                <div className="timeline-icon">
                  <Icon type={node.icon} />
                </div>
                <div className="timeline-date">{node.date}</div>
                <div className="timeline-title">{node.title}</div>
              </div>
            </div>
            
            {/* 阶段标识 */}
            {stageChanges.find(sc => sc.index === index + 0.5) && (
              <div className="timeline-stage-marker">
                <div className="timeline-stage-label">{stageChanges.find(sc => sc.index === index + 0.5)!.stage}</div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default Timeline;